import { assertOfflineContext } from "@/lib/offline/context";
import type { LibraryContext } from "@/native-shell/library";
import { openCompanionDb } from "./device-database";
import { UUID } from "./outings";
import {
  validFeedbackCommand,
  validFeedbackSettingsCommand,
  type OutingFeedbackCommand,
  type FeedbackSettingsCommand,
} from "./outing-feedback";

export const FEEDBACK_DRAFTS_CHANGED = "peakwave-feedback-drafts-changed";
export type FeedbackCommand = OutingFeedbackCommand | FeedbackSettingsCommand;
export type FeedbackDraft = {
  key: string;
  id: string;
  owner: string;
  revision: number;
  command: FeedbackCommand;
};
const STORE = "feedback-drafts";
export const feedbackDraftId = (command: FeedbackCommand) =>
  "outingId" in command ? command.outingId : "settings";
function valid(row: unknown, owner: string): row is FeedbackDraft {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  const r = row as Record<string, unknown>;
  return (
    Object.keys(r).length === 5 &&
    r.owner === owner &&
    typeof r.revision === "number" &&
    Number.isSafeInteger(r.revision) &&
    r.revision > 0 &&
    (validFeedbackCommand(r.command) ||
      validFeedbackSettingsCommand(r.command)) &&
    r.command.owner === owner &&
    r.id === feedbackDraftId(r.command) &&
    r.key === `${owner}:${r.id}`
  );
}
export async function listFeedbackDrafts(
  context: LibraryContext,
): Promise<FeedbackDraft[]> {
  await assertOfflineContext(context);
  if (!UUID.test(context.owner)) return [];
  const db = await openCompanionDb();
  try {
    const rows = await new Promise<unknown[]>((resolve, reject) => {
      const q = db
        .transaction(STORE)
        .objectStore(STORE)
        .index("owner")
        .getAll(context.owner, 52);
      q.onsuccess = () => resolve(q.result);
      q.onerror = () => reject(q.error);
    });
    await assertOfflineContext(context);
    if (
      rows.length > 51 ||
      !rows.every((r) => valid(r, context.owner)) ||
      new TextEncoder().encode(JSON.stringify(rows)).byteLength > 65536
    )
      throw new Error(
        "Device feedback could not be read. Keep your form open and retry.",
      );
    return rows;
  } finally {
    db.close();
  }
}
/** Atomic local CAS before sending. An uncertain network result retains this exact command. */
export async function saveFeedbackDraft(
  context: LibraryContext,
  command: FeedbackCommand,
  expectedRevision: number,
  expectedMutationId: string | null = null,
): Promise<FeedbackDraft> {
  if (
    !(validFeedbackCommand(command) || validFeedbackSettingsCommand(command)) ||
    command.owner !== context.owner
  )
    throw new Error("Review your account and answer first.");
  return (await change(
    context,
    feedbackDraftId(command),
    expectedRevision,
    command,
    expectedMutationId,
  ))!;
}
export async function removeFeedbackDraft(
  context: LibraryContext,
  id: string,
  expectedRevision: number,
  expectedMutationId: string,
): Promise<void> {
  await change(context, id, expectedRevision, null, expectedMutationId);
}
async function change(
  context: LibraryContext,
  id: string,
  expectedRevision: number,
  command: FeedbackCommand | null,
  expectedMutationId: string | null,
): Promise<FeedbackDraft | null> {
  await assertOfflineContext(context);
  if (
    !UUID.test(context.owner) ||
    (id !== "settings" && !UUID.test(id)) ||
    !Number.isSafeInteger(expectedRevision) ||
    expectedRevision < 0 ||
    (expectedMutationId !== null && !UUID.test(expectedMutationId))
  )
    throw new Error("Reopen private feedback before editing.");
  const db = await openCompanionDb();
  let failure: Error | null = null;
  try {
    const result = await new Promise<FeedbackDraft | null>(
      (resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite"),
          store = tx.objectStore(STORE),
          q = store.index("owner").getAll(context.owner, 52);
        let next: FeedbackDraft | null = null;
        tx.oncomplete = () => resolve(next);
        tx.onerror = tx.onabort = () =>
          reject(
            failure ??
              new Error(
                "Device storage could not save feedback. Keep your form open, free some space and retry.",
              ),
          );
        q.onsuccess = () => {
          try {
            const rows: unknown[] = q.result;
            if (rows.length > 51 || !rows.every((r) => valid(r, context.owner)))
              throw new Error("Device feedback could not be checked.");
            const old = rows.find((r) => r.id === id);
            if (
              (old?.revision ?? 0) !== expectedRevision ||
              (old?.command.mutationId ?? null) !== expectedMutationId
            )
              throw new Error(
                "This device answer changed in another tab. Reopen its latest draft before continuing.",
              );
            next = command
              ? {
                  key: `${context.owner}:${id}`,
                  id,
                  owner: context.owner,
                  revision: expectedRevision + 1,
                  command,
                }
              : null;
            const all = [
              ...rows.filter((r) => r.id !== id),
              ...(next ? [next] : []),
            ];
            if (
              all.length > 51 ||
              new TextEncoder().encode(JSON.stringify(all)).byteLength > 65536
            )
              throw new Error(
                "Feedback draft storage is full. Resolve or discard an old pending answer and retry.",
              );
            if (next) store.put(next);
            else store.delete(`${context.owner}:${id}`);
          } catch (error) {
            failure =
              error instanceof Error && error.name === "QuotaExceededError"
                ? new Error(
                    "Device storage is full. Free some space and retry; your answer is still in this form.",
                  )
                : error instanceof Error
                  ? error
                  : new Error("Feedback draft was not saved.");
            tx.abort();
          }
        };
      },
    );
    await assertOfflineContext(context);
    window.dispatchEvent(new Event(FEEDBACK_DRAFTS_CHANGED));
    return result;
  } finally {
    db.close();
  }
}
export async function deleteAccountFeedbackDrafts(
  context: LibraryContext,
): Promise<void> {
  await assertOfflineContext(context);
  const db = await openCompanionDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite"),
        q = tx.objectStore(STORE).index("owner").openCursor(context.owner);
      q.onsuccess = () => {
        const c = q.result;
        if (c) {
          c.delete();
          c.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = tx.onabort = () => reject(tx.error);
    });
    await assertOfflineContext(context);
  } finally {
    db.close();
  }
}
